
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CompareCardContext } from "../../../components/compare/CompareCard";
import Button, { ButtonSquare } from "../../../components/design/buttons/Button";
import Footer from "../../../components/footer/footer";
import { getPlanbyId } from "../../../services/stepper.service";
import './plan.css';
import Header from "../../../components/header/header";

import { FiGlobe } from 'react-icons/fi';
import { HiOutlinePhone, HiOutlineUserGroup } from 'react-icons/hi';
import { FiMessageSquare } from 'react-icons/fi';
import Modal from 'react-bootstrap/Modal';
import { TbDeviceSim } from "react-icons/tb";
import DownloadSection from "../../landing/components/download";
import { getProvider } from "../../../services/providers.service";
import {
    planToItem,
    trackAddToCompare,
    trackBeginCheckout,
    trackViewItem,
} from "../../../tools/tracking";
import { BiQrScan } from "react-icons/bi";
import Rs from "../../../components/design/rs";
import { roundUpToTwoDecimals } from "../../../tools/AssetHelpers";

const PlanScreen = () => {
    const navigate = useNavigate();
    const id = useParams().id;
    const [dataHeader, setDataHeader] = React.useState<any>(null);
    const {addToCart} = useContext(CompareCardContext);
    const {t, i18n} = useTranslation();
    const [show, setShow] = React.useState(false);
    const [showSim, setShowSim] = React.useState(false);
    const handleClose = () => setShow(false);
    const handleCloseSim = () => setShowSim(false);
    const [provider, SetProvider] = React.useState<any>();
    const [plan, setPlan] = React.useState<any>(null);
    const [acceptMnp, setAcceptMnp] = React.useState(false);
    const [requiresNumberReservation, setRequiresNumberReservation] = React.useState(false);
    const rawPlan = React.useRef<any>(null);
    const trackedPlanId = React.useRef<string | null>(null);

    /**
     * Buy / port-in leave for /sim, which bounces unauthenticated users to
     * /login2 — so begin_checkout has to fire here, before navigating, or the
     * event is lost for every logged-out user.
     */
    const beginCheckout = (numberType: "new" | "port_in") => {
        if (!rawPlan.current) return;
        trackBeginCheckout({
            item: planToItem(rawPlan.current, { provider }),
            numberType,
        });
    };

    const goBuy = () => {
        beginCheckout("new");
        navigate("/sim?id=" + id + "&requiresNumberReservation=" + requiresNumberReservation);
    };

    const goPortIn = () => {
        beginCheckout("port_in");
        navigate("/sim?id=" + id + "&bringNumber=true");
    };

    const compareAndBack = () => {
        if (rawPlan.current) trackAddToCompare(planToItem(rawPlan.current, { provider }));
        addToCart(dataHeader, '');
        navigate(-1);
    };

    const feachData = () => {
        getPlanbyId(id, i18n.language)
        .then(async (a: any) => {
            const data = a.data.returnData;

            const providerId = data.providerId;
            setAcceptMnp(data.acceptMnp);
            setRequiresNumberReservation(data.requiresNumberReservation);
            const providers = await getProvider(providerId, i18n.language);
            const resolvedProvider = providers.filter((a: any) => a.id === providerId)[0];
            SetProvider(resolvedProvider);

            // kept for the Buy CTAs below, which need the raw plan shape
            rawPlan.current = data;
            // feachData re-runs on language change; the item was already viewed
            if (trackedPlanId.current !== String(data.id)) {
                trackedPlanId.current = String(data.id);
                trackViewItem(planToItem(data, { provider: resolvedProvider }));
            }

            setDataHeader({
                title: data.name,
                image: data.imageUrl,
                price: data.price,
                newPrice: data.discountRate === 0 ? data.price : data.price - (data.price * data.discountRate),
                hasDiscount: data.discountRate === 0 ? false : true,
                providerName: resolvedProvider.name,
                providerId: providerId,
                isPrepaid: data.contractTypeId === 1,
                typeName: t(data.contractTypeId === 1 ? t("details.prepaid") : t("details.postpaid")),
                id: data.id,
                likes: data.likes || 0,
                issueThroughTygo: data.issueThroughTygo,
            } as any);
            setPlan(data);
        }
        )
    }

    useEffect(() => {
        feachData();
    }, [id, i18n.language])

    if(!dataHeader || !plan || !provider) return (<div>
        {/* Loading... */}
        </div>
    )

    /** -1 means unlimited on every quota field; 0 / null means the plan simply
     *  does not carry that quota, which reads better as "Not included". */
    const quota = (value: number, unit?: any) => {
        if (value === -1) return t("plans.unlimited");
        if (!value) return t("details.not_included");
        return value.toLocaleString() + (unit ? " " + unit : "");
    };
    const data_ = (value: number) => quota(value === -1 ? -1 : value / 1000, t("plans.gb"));

    const canBuy = dataHeader.issueThroughTygo && dataHeader.providerId != 7;
    const discountPercent = Math.round(plan.discountRate * 100);
    const price = roundUpToTwoDecimals(Number(dataHeader.newPrice));
    const socialApps = typeof plan?.features === "string" && plan.features.split(":").length > 1
        ? plan.features.split(":")[1].trim().replace(" and ", ", ").split(",")
            .map((app: string) => app.trim().toLowerCase().replace("\"", ""))
        : [];

    const highlights = [
        {
            icon: <FiGlobe />,
            value: data_(plan.localInternt),
            label: t("details.local_internet"),
        },
        {
            icon: <HiOutlineUserGroup />,
            value: data_(plan.socialMedia),
            label: t("details.social_apps"),
        },
        {
            icon: <HiOutlinePhone />,
            value: quota(plan.minutesInSideProvider, t("details.minutes_short")),
            label: t("details.calls_inside_network"),
        },
        {
            icon: <FiMessageSquare />,
            value: quota(plan.smSForCurrentProvider),
            label: t("details.text_messages"),
        },
    ];

    const sections = [
        {
            title: t("details.internetTitle"),
            rows: [
                { label: t("details.local_data"), value: data_(plan.localInternt) },
                {
                    label: t("details.social_apps"),
                    value: data_(plan.socialMedia),
                    body: socialApps.length > 0 && (
                        <div className="pdp-apps">
                            {socialApps.map((app: string, index: number) => (
                                <img src={"/media/svgs/media/" + app + ".svg"} alt={app} key={index} />
                            ))}
                        </div>
                    ),
                },
            ],
        },
        {
            title: t("details.callsTitle"),
            rows: [
                { label: t("details.inside_network"), value: quota(plan.minutesInSideProvider, t("plans.minutes")) },
                { label: t("details.calls_international"), value: quota(plan.internationalCalls, t("plans.minutes")) },
            ],
        },
        {
            title: t("details.messagesTitle"),
            rows: [
                { label: t("details.text_messages"), value: quota(plan.smSForCurrentProvider, t("details.sms_unit")) },
            ],
        },
        {
            title: t("details.plan_details"),
            rows: [
                { label: t("details.validity"), value: plan.validityTimeInDays.toLocaleString() + " " + t("plans.days") },
                { label: t("details.type"), value: dataHeader.typeName },
                { label: t("details.provider"), value: dataHeader.providerName },
                { label: t("details.taxes"), value: t("details.included") },
            ],
        },
    ];

    const priceTag = (
        <div className="pdp-price">
            {dataHeader.hasDiscount &&
                <span className="pdp-price-old">{Number(dataHeader.price).toFixed(2)}</span>}
            <span className="pdp-price-now">
                <Rs size={26} />
                {price}
            </span>
            <span className="pdp-price-period">{t("details.per_month")}</span>
        </div>
    );

    return (
        <div className="pdp-page">

            <div className="container my-2 px-3">
                <Header />
            </div>

            <div className="container pdp-detail px-3">

                <button type="button" className="pdp-back" onClick={() => navigate(-1)}>
                    <span className="pdp-back-arrow">{i18n.language === "ar" ? "→" : "←"}</span>
                    {t("details.back_to_plans")}
                </button>

                <section className="pdp-card pdp-hero">
                    <div className="pdp-hero-top">
                        <div className="pdp-hero-brand">
                            <img src={provider.imagePathSmall || dataHeader.image} alt={dataHeader.providerName} />
                            <span>{t("details.sim_plan", { type: dataHeader.typeName })}</span>
                        </div>
                        <div className="pdp-hero-pricing">
                            {dataHeader.hasDiscount && discountPercent > 0 &&
                                <span className="pdp-save-badge">
                                    {t("details.save_badge", { percent: discountPercent })}
                                </span>}
                            {priceTag}
                        </div>
                    </div>

                    <h1 className="pdp-hero-title">{dataHeader.title}</h1>

                    <div className="pdp-pills">
                        <span className="pdp-pill">{t("details.valid_renews", { days: plan.validityTimeInDays })}</span>
                        <span className="pdp-pill">{t("details.taxes_included")}</span>
                        {acceptMnp && <span className="pdp-pill">{t("details.keep_your_number")}</span>}
                    </div>

                    <div className="pdp-hero-divider"></div>

                    <div className="pdp-hero-actions">
                        <div className="pdp-hero-ctas">
                            {canBuy &&
                                <Button
                                    inner={<span className="pdp-cta-label">{t("details.buy")}</span>}
                                    onClick={goBuy}
                                    color="primary"
                                />}
                            {acceptMnp &&
                                <Button
                                    inner={<span className="pdp-cta-label">
                                        {t("details.Bringyournumberto") + " " + provider.name}
                                    </span>}
                                    onClick={goPortIn}
                                    color="outline"
                                    style={{
                                        color: provider.primaryColor,
                                        borderColor: provider.primaryColor,
                                    }}
                                />}
                        </div>
                        <button type="button" className="pdp-compare-link" onClick={compareAndBack}>
                            {t("details.compare")} {i18n.language === "ar" ? "←" : "→"}
                        </button>
                    </div>
                </section>

                <div className="pdp-highlights">
                    {highlights.map((item, index) => (
                        <div className="pdp-card pdp-highlight" key={index}>
                            <div className="pdp-highlight-icon">{item.icon}</div>
                            <strong className="pdp-highlight-value">{item.value}</strong>
                            <p className="pdp-highlight-label">{item.label}</p>
                        </div>
                    ))}
                </div>

                <section className="pdp-card pdp-included">
                    <h2 className="pdp-section-title">{t("details.whats_included")}</h2>
                    <div className="pdp-included-grid">
                        {sections.map((section, index) => (
                            <div className="pdp-spec-group" key={index}>
                                <h3 className="pdp-spec-title">{section.title}</h3>
                                {section.rows.map((row: any, rowIndex: number) => (
                                    <div className="pdp-spec-row" key={rowIndex}>
                                        <div className="pdp-spec-line">
                                            <span className="pdp-spec-label">{row.label}</span>
                                            <span className={"pdp-spec-value" +
                                                (row.value === t("details.not_included") ? " pdp-spec-value-muted" : "")}>
                                                {row.value}
                                            </span>
                                        </div>
                                        {row.body}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </section>

                {canBuy &&
                    <div className="pdp-card pdp-sticky-bar">
                        <div className="pdp-sticky-info">
                            <strong>{dataHeader.title}</strong>
                            <div className="pdp-price pdp-price-sm">
                                {dataHeader.hasDiscount &&
                                    <span className="pdp-price-old">{Number(dataHeader.price).toFixed(2)}</span>}
                                <span className="pdp-price-now">
                                    <Rs size={16} />
                                    {price}
                                </span>
                                <span className="pdp-price-period">{t("details.per_month")}</span>
                            </div>
                        </div>
                        <Button
                            inner={<span className="pdp-cta-label">{t("details.buy")}</span>}
                            onClick={goBuy}
                            color="primary"
                        />
                    </div>}

            </div>

            <DownloadSection />
            <Footer />

            <Modal show={show} onHide={handleClose}>
                <Modal.Body>
                    <h1 className='mb-2 mt-2 mt-lg-3 mb-lg-5 text-center'>{t("footer.title")}</h1>
                    <p className='my-2 my-lg-5 text-center'>
                        {t("footer.sub_title")}
                    </p>
                    <div className="d-flex justify-content-center">
                        <div className='m-2'>
                            <a href="https://apps.apple.com/qa/app/tygo/id1619973559?platform=iphone](https://apps.apple.com/qa/app/tygo/id1619973559?platform=iphone)" target="_blank">
                                <img src="/media/buttons/apple.svg" width={124} />
                            </a>
                        </div>
                        <div className='m-2'>
                            <a href='https://play.google.com/store/apps/details?id=app.tygo.sa' target="_blank">
                                <img src="/media/buttons/google.svg" width={124} />
                            </a>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <Modal show={showSim} onHide={handleCloseSim}>
                <Modal.Body>
                    <div style={{ direction: i18n.language === "ar" ? "rtl" : "ltr" }}>
                        <h1>{t("local_sim.select_type.title")}</h1>
                        <div className="d-flex  gap-3">
                            <div className="w-100">
                                <ButtonSquare inner={
                                    <div className="d-flex align-items-center  gap-2 justify-content-center" style={{flexDirection: "column"}}>
                                        <BiQrScan className="icon_det" style={{color: '#3EB6AE', fontSize: '1.5rem'}} />
                                        <span>{t("local_sim.select_type.options.esim")}</span>
                                    </div>
                                } color="primary-outline-primary p-5 m-0" isfullWidth={true} isrounded={true} onClick={()=>navigate("/sim?id="+id+"&type=esim")}></ButtonSquare>
                            </div>
                            <div className="w-100">
                                <ButtonSquare inner={<div className="d-flex align-items-center  gap-2 justify-content-center" style={{flexDirection: "column"}}>
                                        <TbDeviceSim className="icon_det" style={{color: '#3EB6AE', fontSize: '1.5rem'}} />
                                        <span>{t("local_sim.select_type.options.sim")}</span>
                                    </div>} color="primary-outline-primary p-5 me-0" isfullWidth={true} isrounded={true} onClick={()=>navigate("/sim?id="+id+"&type=sim")}></ButtonSquare>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};


export default PlanScreen;
